/**
 * Concept Relationship Graph Adapter
 * 
 * Transforms graph artifact into UI model for visualization.
 * 
 * Constraints:
 * - proofOnly = true
 * - executionAuthority = false
 * - advisoryOnly = true
 * - No graph mutation
 * - No relationship creation
 * - No merge, rename, delete, admit, promote, resolve, auto-fix controls
 */

import * as fs from 'fs';
import * as path from 'path';

// ============================================================================
// Types
// ============================================================================

export interface GraphNode {
  nodeId: string;
  nodeType: string;
  label: string;
  conceptId: string | null;
  sourceArtifacts: string[];
  confidence: string;
  uncertaintyNotes: string[];
}

export interface GraphEdge {
  edgeId: string;
  sourceNodeId: string;
  relationshipTypeId: string;
  relationshipLabel: string;
  targetNodeId: string;
  category: string;
  directional: boolean;
  inverseRelationship: string | null;
  evidenceSources: string[];
  confidence: string;
  uncertaintyNotes: string[];
  advisoryOnly: boolean;
  executionAuthority: boolean;
}

export interface ConceptRelationshipGraph {
  graphId: string;
  generatedAt: string;
  nodes: GraphNode[];
  edges: GraphEdge[];
  sourceArtifacts: string[];
  unresolvedEdges: Array<{
    edgeId: string;
    sourceLabel: string;
    relationshipLabel: string;
    targetLabel: string;
    reason: string;
  }>;
  confidenceSummary: {
    highConfidenceEdges: number;
    mediumConfidenceEdges: number;
    lowConfidenceEdges: number;
    totalEdges: number;
  };
  proofOnly: boolean;
  executionAuthority: boolean;
  advisoryOnly: boolean;
}

export interface GraphVisualizationModel {
  graphId: string;
  generatedAt: string;
  summary: {
    nodeCount: number;
    edgeCount: number;
    unresolvedEdgeCount: number;
    highConfidenceEdges: number;
    mediumConfidenceEdges: number;
    lowConfidenceEdges: number;
  };
  nodes: GraphNode[];
  edges: GraphEdge[];
  categories: string[];
  confidenceDistribution: {
    high: number;
    medium: number;
    low: number;
  };
  evidenceSources: string[];
  unresolvedEdges: Array<{
    edgeId: string;
    sourceLabel: string;
    relationshipLabel: string;
    targetLabel: string;
    reason: string;
  }>;
  safetyBoundaries: {
    proofOnly: boolean;
    executionAuthority: boolean;
    advisoryOnly: boolean;
    noGraphMutation: boolean;
    noRelationshipCreation: boolean;
    noMerge: boolean;
    noRename: boolean;
    noDelete: boolean;
    noAdmit: boolean;
    noPromote: boolean;
    noResolve: boolean;
    noAutoFix: boolean;
    noCreateRelationship: boolean;
    noRewriteGraph: boolean;
    noWriteMemory: boolean;
  };
}

// ============================================================================
// Adapter
// ============================================================================

export class ConceptRelationshipGraphAdapter {
  private graphPath: string;

  constructor(graphPath?: string) {
    this.graphPath = graphPath || path.join(
      __dirname,
      '../../../artifacts/relationships/concept-relationship-graph-v1.json'
    );
  }

  /**
   * Load graph from disk
   */
  loadGraph(): ConceptRelationshipGraph {
    try {
      const graphContent = fs.readFileSync(this.graphPath, 'utf-8');
      const graph = JSON.parse(graphContent) as ConceptRelationshipGraph;
      return graph;
    } catch (error) {
      throw new Error(`Failed to load concept relationship graph: ${error}`);
    }
  }

  /**
   * Transform graph into visualization model
   */
  adaptToVisualizationModel(graph: ConceptRelationshipGraph): GraphVisualizationModel {
    const categories = this.extractCategories(graph.edges);
    const evidenceSources = this.extractEvidenceSources(graph.edges);

    return {
      graphId: graph.graphId,
      generatedAt: graph.generatedAt,
      summary: {
        nodeCount: graph.nodes.length,
        edgeCount: graph.edges.length,
        unresolvedEdgeCount: graph.unresolvedEdges.length,
        highConfidenceEdges: graph.confidenceSummary.highConfidenceEdges,
        mediumConfidenceEdges: graph.confidenceSummary.mediumConfidenceEdges,
        lowConfidenceEdges: graph.confidenceSummary.lowConfidenceEdges,
      },
      nodes: graph.nodes,
      edges: graph.edges,
      categories,
      confidenceDistribution: {
        high: graph.confidenceSummary.highConfidenceEdges,
        medium: graph.confidenceSummary.mediumConfidenceEdges,
        low: graph.confidenceSummary.lowConfidenceEdges,
      },
      evidenceSources,
      unresolvedEdges: graph.unresolvedEdges,
      safetyBoundaries: {
        proofOnly: graph.proofOnly,
        executionAuthority: graph.executionAuthority,
        advisoryOnly: graph.advisoryOnly,
        noGraphMutation: true,
        noRelationshipCreation: true,
        noMerge: true,
        noRename: true,
        noDelete: true,
        noAdmit: true,
        noPromote: true,
        noResolve: true,
        noAutoFix: true,
        noCreateRelationship: true,
        noRewriteGraph: true,
        noWriteMemory: true,
      },
    };
  }

  /**
   * Extract unique categories from edges
   */
  private extractCategories(edges: GraphEdge[]): string[] {
    const categorySet = new Set<string>();
    for (const edge of edges) {
      categorySet.add(edge.category);
    }
    return Array.from(categorySet).sort();
  }

  /**
   * Extract unique evidence sources from edges
   */
  private extractEvidenceSources(edges: GraphEdge[]): string[] {
    const sourceSet = new Set<string>();
    for (const edge of edges) {
      for (const source of edge.evidenceSources) {
        sourceSet.add(source);
      }
    }
    return Array.from(sourceSet).sort();
  }

  /**
   * Load and adapt graph in one step
   */
  loadAndAdapt(): GraphVisualizationModel {
    const graph = this.loadGraph();
    return this.adaptToVisualizationModel(graph);
  }
}
